import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Newspaper, 
  ExternalLink, 
  Clock, 
  TrendingUp, 
  Zap, 
  RefreshCw,
  AlertCircle,
  Calendar
} from 'lucide-react';

const HotOffThePress = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const fetchNews = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${backendUrl}/api/news/latest`);
      if (response.ok) {
        const newsData = await response.json();
        setArticles(newsData);
        setLastUpdated(new Date());
      } else {
        setError('Failed to load latest news');
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setError('Unable to connect to news service');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    
    // Auto-refresh every 15 minutes
    const interval = setInterval(fetchNews, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleArticleClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-red-600 via-orange-600 to-red-700 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-full p-4">
              <Zap className="h-12 w-12 text-red-600" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Hot off the Press 🔥
          </h1>
          <p className="text-xl text-red-100 mb-4 max-w-3xl mx-auto">
            Latest breaking news from The Adviser - your trusted source for mortgage and finance industry updates
          </p>
          <div className="flex items-center justify-center gap-2 text-red-200">
            <TrendingUp className="h-5 w-5" />
            <span>Live updates from theadviser.com.au</span>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Newspaper className="h-5 w-5" />
                <span className="font-medium">Industry News Feed</span>
              </div>
              {lastUpdated && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>Updated {formatTimeAgo(lastUpdated)}</span>
                </div>
              )}
            </div>
            
            <Button
              onClick={fetchNews}
              disabled={loading}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* News Content */}
      <div className="flex-1 py-8">
        <div className="max-w-6xl mx-auto px-4">
          {error && (
            <div className="mb-8">
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 text-red-700">
                    <AlertCircle className="h-5 w-5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Unable to load news</div>
                      <div className="text-sm text-red-600 mt-1">{error}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {loading && articles.length === 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, index) => (
                <Card key={index} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-48 bg-gray-200 rounded-md mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((article, index) => (
                <Card 
                  key={index} 
                  className="cursor-pointer hover:shadow-lg transition-shadow group"
                  onClick={() => handleArticleClick(article.url)}
                >
                  {article.image_url && (
                    <div className="relative overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {article.category && (
                        <div className="absolute top-3 left-3">
                          <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                            {article.category}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Calendar className="h-3 w-3" />
                      <span>{article.published_date}</span>
                      <span className="text-red-600 ml-auto">
                        <ExternalLink className="h-3 w-3" />
                      </span>
                    </div>
                    
                    <h3 className="font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors">
                      {article.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {article.summary}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        The Adviser
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 p-0 h-auto"
                      >
                        Read More
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {articles.length === 0 && !loading && !error && (
            <div className="text-center py-12">
              <Newspaper className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No articles available</h3>
              <p className="text-gray-600">
                Check back later for the latest industry news and updates.
              </p>
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-12 text-center">
            <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
              <CardContent className="p-8">
                <div className="flex justify-center mb-4">
                  <TrendingUp className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Stay Informed with The Adviser
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Get the latest breaking news, industry analysis, and expert insights 
                  delivered directly from Australia's leading mortgage and finance publication.
                </p>
                <Button
                  onClick={() => window.open('https://www.theadviser.com.au/', '_blank')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Visit The Adviser
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HotOffThePress;